import { useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'

type OrgPermissionId =
  | 'members.view' | 'members.invite' | 'members.remove' | 'members.edit_roles'
  | 'domains.view' | 'domains.manage'
  | 'sso.view' | 'sso.manage'
  | 'apps.view' | 'apps.manage'
  | 'settings.view' | 'settings.manage'
  | 'audit.view'
  | 'analytics.view'

interface UseOrgPermissionsReturn {
  permissions: Set<string>
  loading: boolean
  error: string | null
  loadPermissions: (organizationId: string) => Promise<void>
  hasPermission: (permission: OrgPermissionId) => boolean
  canManageMembers: boolean
  canManageDomains: boolean
  canManageSso: boolean
  canManageApps: boolean
  canManageSettings: boolean
  canViewAudit: boolean
  canViewAnalytics: boolean
}

export function useOrgPermissions(): UseOrgPermissionsReturn {
  const [permissions, setPermissions] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadPermissions = useCallback(async (organizationId: string) => {
    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase.rpc('get_my_org_permissions', {
        p_organization_id: organizationId
      })

      if (fetchError) throw fetchError

      const permSet = new Set<string>(
        (data || []).map((p: { permission_id: string }) => p.permission_id)
      )
      setPermissions(permSet)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load permissions')
      setPermissions(new Set())
    } finally {
      setLoading(false)
    }
  }, [])

  const hasPermission = useCallback((permission: OrgPermissionId): boolean => {
    return permissions.has(permission)
  }, [permissions])

  // Convenience getters
  const canManageMembers = permissions.has('members.invite') || permissions.has('members.remove') || permissions.has('members.edit_roles')
  const canManageDomains = permissions.has('domains.manage')
  const canManageSso = permissions.has('sso.manage')
  const canManageApps = permissions.has('apps.manage')
  const canManageSettings = permissions.has('settings.manage')
  const canViewAudit = permissions.has('audit.view')
  const canViewAnalytics = permissions.has('analytics.view')

  return {
    permissions,
    loading,
    error,
    loadPermissions,
    hasPermission,
    canManageMembers,
    canManageDomains,
    canManageSso,
    canManageApps,
    canManageSettings,
    canViewAudit,
    canViewAnalytics
  }
}
